import { Box, CircularProgress, IconButton, Pagination, Paper, Typography } from "@mui/material"
import { ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useParams, useRouter, useSearch } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { ConfirmationDialog } from "@/components/common/Modals"
import { PermissionActionType } from "@/constants/permissions"
import type { Product, UpdateSubmissionItemInput } from "@/graphql/codegen/graphql"
import { PriceChangeSubmissionSource, PriceChangeSubmissionStatus, PriceChangeType } from "@/graphql/codegen/graphql"
import { useNavigationGuard } from "@/hooks/useNavigationGuard"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { AddSpecialPriceDialog } from "@/screens/Pricing/components/dialogs"
import ReviewSubmissionHeader from "@/screens/Pricing/components/ReviewSubmissionHeader"
import type { ProductPricingTableHandle } from "@/screens/Pricing/components/tables"
import { ProductPricingTable } from "@/screens/Pricing/components/tables"
import { PriceChangeTableVariant, PricingDialogs } from "@/screens/Pricing/constants"
import { useDeleteSubmissionProduct } from "@/screens/Pricing/hooks/api/useDeleteSubmissionProduct"
import { useGetPriceChangeSubmission } from "@/screens/Pricing/hooks/useGetPriceChangeSubmission"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog"
import { usePriceChangeTablesStore, usePriceTableEditsStore, useSubmissionUpdatesStore } from "@/screens/Pricing/stores"
import { calculateInvalidTiersCount } from "@/screens/Pricing/utils/priceChangeSubmit"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

const REVIEW_ROUTE = "/app/pricing/$priceChangeId/review"

const ReviewSubmissionContainer: FC = () => {
  const { priceChangeId } = useParams({ from: REVIEW_ROUTE })
  const { page = 1 } = useSearch({ from: REVIEW_ROUTE })

  const navigate = useNavigate({ from: REVIEW_ROUTE })
  const router = useRouter()

  const { priceChangeSubmission, quoteProductByProductId, total, pageCount, isFetching, refetch } =
    useGetPriceChangeSubmission({ priceChangeId, page })
  const { deleteSubmissionProduct, isDeleting } = useDeleteSubmissionProduct()

  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const { tableChanges, selectedCounts, resetVersion, setTableChange, setSelectedCount, reset } =
    usePriceChangeTablesStore(
      useShallow((s) => ({
        tableChanges: s.tableChanges,
        selectedCounts: s.selectedCounts,
        resetVersion: s.resetVersion,
        setTableChange: s.setTableChange,
        setSelectedCount: s.setSelectedCount,
        reset: s.reset,
      }))
    )

  const clearEdits = usePriceTableEditsStore((s) => s.clearEdits)
  const priceChangeEdits = usePriceTableEditsStore((s) => s.edits)

  const modifiedTiersCount = usePriceTableEditsStore((s) => Object.keys(s.edits).length)
  const totalInvalidCount = useMemo(() => calculateInvalidTiersCount(priceChangeEdits), [priceChangeEdits])

  const { captureItems, setSubmission, clearSubmissionUpdates, pendingUpdatesCount } = useSubmissionUpdatesStore(
    useShallow((s) => ({
      captureItems: s.captureItems,
      setSubmission: s.setSubmission,
      clearSubmissionUpdates: s.clear,
      pendingUpdatesCount: Object.keys(s.itemsByCustomerRowId).length,
    }))
  )

  const clearPendingChanges = useCallback(() => {
    clearEdits()
    reset()
    clearSubmissionUpdates()
  }, [clearEdits, reset, clearSubmissionUpdates])

  const tableRefs = useRef<Record<string, ProductPricingTableHandle | null>>({})
  const hasAnyChanges = pendingUpdatesCount > 0 || Object.values(tableChanges).some(Boolean)

  const { disableBlock } = useNavigationGuard({
    shouldBlock: hasAnyChanges,
    onNavigationConfirmHandler: clearPendingChanges,
  })
  const { isDialogOpen, setIsDialogOpen, setSelectedProduct, selectedProduct } = usePricingDialog()

  const { hasPermissions } = usePermissionManager()

  const hasMaintenancePermission = hasPermissions([
    PermissionActionType.PRICING_MANAGER,
    PermissionActionType.PRICING_MAINTENANCE,
  ])

  const isResolved = useMemo(
    () => priceChangeSubmission?.status !== PriceChangeSubmissionStatus.Submitted,
    [priceChangeSubmission]
  )
  const readonly = isResolved || !hasMaintenancePermission

  const prevSubmissionIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevSubmissionIdRef.current === priceChangeId) return
    prevSubmissionIdRef.current = priceChangeId
    clearPendingChanges()
    setSubmission(priceChangeId)
  }, [priceChangeId, clearPendingChanges, setSubmission])

  useEffect(() => () => clearPendingChanges(), [clearPendingChanges])

  const handleSelectionChange = useCallback(
    (id: string, info: { selected: number; total: number }) => {
      setSelectedCount(id, info.selected)
    },
    [setSelectedCount]
  )

  const handleChangesChange = useCallback(
    (id: string, hasChanges: boolean) => setTableChange(id, hasChanges),
    [setTableChange]
  )

  const flushPendingEdits = async () => {
    await Promise.all(Object.values(tableRefs.current).map((tableRef) => tableRef?.flushPendingEdits()))
  }

  const collectCurrentPageItems = async (): Promise<UpdateSubmissionItemInput[]> => {
    await flushPendingEdits()
    const editedGroupIds = new Set(Object.values(usePriceTableEditsStore.getState().edits).map((edit) => edit.group.id))

    return (priceChangeSubmission?.priceChangeItems ?? [])
      .filter(
        (priceChangeItem) =>
          tableChanges[priceChangeItem.id] || priceChangeItem.groups.some((group) => editedGroupIds.has(group.id))
      )
      .flatMap((priceChangeItem) => tableRefs.current[priceChangeItem.id]?.getUpdateItems() ?? [])
  }

  const handlePageChange = async (_: ChangeEvent<unknown>, value: number) => {
    if (!readonly) {
      captureItems(await collectCurrentPageItems())
    }

    navigate({
      search: (prev) => ({ ...prev, page: value === 1 ? undefined : value }),
      replace: true,
    })
  }

  const handleDeny = () => {
    clearPendingChanges()
    refetch()
  }

  const handleSaveAndExit = async () => {
    disableBlock()
    await navigate({
      to: `/app/pricing/history/${
        priceChangeSubmission?.changeType === PriceChangeType.PurchasingCost ? "purchase-cost" : "price-change"
      }`,
    })
  }

  const handleApprove = () => {
    clearPendingChanges()
    refetch()
  }

  const handleInvalidDatesReset = () => {
    Object.values(tableRefs.current).forEach((tableRef) => tableRef?.resetInvalidDates())
  }

  const handleBack = () => {
    router.history.back()
  }

  const handleRevert = async (submissionId: string) => {
    await navigate({ to: `/app/pricing/${submissionId}/review` })
  }

  const handleRemoveSubmissionProduct = async (product: Product) => {
    if (!priceChangeSubmission?.id) {
      return
    }

    const isDeleteSuccess = await deleteSubmissionProduct({
      submissionId: priceChangeSubmission.id,
      productId: product.productId,
    })

    if (!isDeleteSuccess) {
      return
    }

    clearPendingChanges()
    refetch()
    handleDialogClose()
  }

  const handleDialogClose = () => {
    setIsDialogOpen(null)
    setSelectedProduct(null)
  }

  if (!priceChangeSubmission) {
    return (
      <Box className='flex items-center justify-center p-16'>
        <CircularProgress />
      </Box>
    )
  }

  const isQuoteSource = priceChangeSubmission.source === PriceChangeSubmissionSource.SalesPipelineQuoteRequest
  const isSellingPrice = priceChangeSubmission.changeType === PriceChangeType.SellingPrice
  const canRemove = !isResolved && priceChangeSubmission.priceChangeItems.length > 1
  const isRevertMode = isResolved && isSellingPrice && !isQuoteSource
  const submissionNoun = isQuoteSource ? "Quote" : "Change"
  const displayId =
    isQuoteSource && priceChangeSubmission.quoteRequest
      ? priceChangeSubmission.quoteRequest.id
      : priceChangeSubmission.changeId

  return (
    <Paper elevation={0} className='flex min-h-0 grow flex-col gap-3 overflow-hidden bg-white'>
      <Box className='flex flex-row items-center gap-3'>
        <IconButton aria-label='back to list' className='rounded bg-gray-200 text-primary' onClick={handleBack}>
          <ChevronLeftOutlined />
        </IconButton>
        <Typography variant='h5' color='primary'>
          {isRevertMode ? "Revert" : "Review"} {submissionNoun} ID {displayId}
        </Typography>
      </Box>

      <ReviewSubmissionHeader
        onApprove={handleApprove}
        onSaveAndExit={handleSaveAndExit}
        onRevert={handleRevert}
        onDeny={handleDeny}
        isResolved={isResolved}
        isSellingPrice={isSellingPrice}
        isDialogOpen={isDialogOpen}
        priceChangeSubmission={priceChangeSubmission}
        setIsDialogOpen={setIsDialogOpen}
        hasMaintenancePermission={hasMaintenancePermission}
        hasAnyChanges={hasAnyChanges}
        selectedCounts={selectedCounts}
        total={total}
        modifiedTiersCount={modifiedTiersCount}
        totalInvalidCount={totalInvalidCount}
        isFetching={isFetching}
        collectCurrentPageItems={collectCurrentPageItems}
        flushPendingEdits={flushPendingEdits}
        onChangesClear={clearPendingChanges}
        onInvalidDatesReset={handleInvalidDatesReset}
      />

      {/* Body */}
      <Box className='flex min-h-0 w-full flex-1 flex-col overflow-y-auto py-2'>
        {isFetching && (
          <Box className='flex flex-1 items-center justify-center p-16'>
            <CircularProgress />
          </Box>
        )}
        {!isFetching &&
          priceChangeSubmission.priceChangeItems.map((priceChangeItem) => (
            <Box key={`${priceChangeItem.id}-${resetVersion}`} className='mb-6'>
              <ProductPricingTable
                ref={(el: ProductPricingTableHandle | null) => {
                  if (el) {
                    tableRefs.current[priceChangeItem.id] = el
                  } else {
                    delete tableRefs.current[priceChangeItem.id]
                  }
                }}
                priceChangeItems={priceChangeItem}
                quoteProduct={quoteProductByProductId.get(priceChangeItem.product.productId)}
                uoms={uoms}
                variant={
                  isResolved ? PriceChangeTableVariant.RevertSubmission : PriceChangeTableVariant.ReviewSubmission
                }
                isQuoteSource={isQuoteSource}
                setDialogOpen={setIsDialogOpen}
                setSelectedProduct={setSelectedProduct}
                onSelectionChange={handleSelectionChange}
                onChangesChange={handleChangesChange}
                readonly={readonly}
                persistEdits={!readonly}
                priceChangeType={priceChangeSubmission.changeType}
                canRemove={canRemove}
              />
            </Box>
          ))}

        <Box className='sticky bottom-[-8px] z-10 flex shrink-0 items-center justify-center bg-white pt-4 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.15)]'>
          <Pagination
            count={Math.max(pageCount, 1)}
            showFirstButton
            showLastButton
            page={page}
            onChange={handlePageChange}
            color='primary'
            data-testid='pricing.review-submission.pagination'
          />
        </Box>
      </Box>

      {isDialogOpen === PricingDialogs.AddSpecialPrice && selectedProduct && (
        <AddSpecialPriceDialog
          onSuccess={() => {
            handleDialogClose()
          }}
          changeType={priceChangeSubmission.changeType}
          onClose={handleDialogClose}
          selectedProduct={selectedProduct}
        />
      )}

      {isDialogOpen === PricingDialogs.RemoveSubmissionProductConfirmation && selectedProduct && (
        <ConfirmationDialog
          title='Are you sure you want to remove this product?'
          content='This action cannot be undone.'
          onClose={handleDialogClose}
          confirmDisabled={isDeleting}
          confirmLabel='Yes, Remove'
          isWarning
          closeLabel='Cancel'
          onConfirm={() => handleRemoveSubmissionProduct(selectedProduct)}
        />
      )}
    </Paper>
  )
}

export default ReviewSubmissionContainer
